import { useMemo } from 'react'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'
import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import type { ActivityModel, ProviderPriceListModel } from '@models/types'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import { generateUrls } from '@hooks/useProviderPriceList'

import ArchiveProviderPriceListModal from '@modals/ArchiveProviderPriceListModal'
import BulkReassignAgenciesModal from '@modals/BulkReassignAgenciesModal'
import CreateOrEditProviderPriceListModal from '@modals/CreateOrEditProviderPriceListModal'
import ForkProviderPriceListModal from '@modals/ForkProviderPriceListModal'
import ManageActivityModal from '@modals/ManageActivityModal'
import UploadEntityAssetsModal from '@modals/UploadEntityAssetsModal'

import PageContent from './components/PageContent'

const defaultState = {
  showArchiveProviderPriceListModal: false,
  showBulkReassignAgenciesModal: false,
  showCreateOrEditProviderPriceListModal: false,
  showForkProviderPriceListModal: false,
  showManageActivityModal: false,
  showUploadEntityAssetsModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    ArchiveProviderPriceListModal: {
      closeModal: () => setState({ showArchiveProviderPriceListModal: false }),
      archiveProviderPriceList: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    BulkReassignAgenciesModal: {
      closeModal: () => setState({ showBulkReassignAgenciesModal: false }),
      bulkReassignAgencies: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditProviderPriceListModal: {
      closeModal: () => setState({ showCreateOrEditProviderPriceListModal: false }),
      updateProviderPriceList: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ForkProviderPriceListModal: {
      closeModal: () => setState({ showForkProviderPriceListModal: false }),
      forkProviderPriceList: async (payload: HandleCallbackActionParams) => {
        const { data, success } = await handleCallbackAction(payload)
        if (success && data){
          const { editProviderPriceListUrl } = generateUrls(data)
          window.location.hash = editProviderPriceListUrl
        }
      },
    },
    ManageActivityModal: {
      closeModal: () => setState({ showManageActivityModal: false }),
    },
    UploadEntityAssetsModal: {
      closeModal: () => setState({ showUploadEntityAssetsModal: false }),
      deleteAttachment: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const Details = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showArchiveProviderPriceListModal,
    showBulkReassignAgenciesModal,
    showCreateOrEditProviderPriceListModal,
    showForkProviderPriceListModal,
    showManageActivityModal,
    showUploadEntityAssetsModal,
  } = state

  const { providerPriceListId } = useNumericParams()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      deleteAttachment: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      showArchiveProviderPriceListModal: (payload: { providerPriceList: ProviderPriceListModel }) => {
        setModalData('ArchiveProviderPriceListModal', payload)
        setState({ showArchiveProviderPriceListModal: true })
      },
      showBulkReassignAgenciesModal: (payload: { providerPriceList: ProviderPriceListModel }) => {
        setModalData('BulkReassignAgenciesModal', payload)
        setState({ showBulkReassignAgenciesModal: true })
      },
      showForkProviderPriceListModal: (payload: { providerPriceList: ProviderPriceListModel }) => {
        setModalData('ForkProviderPriceListModal', payload)
        setState({ showForkProviderPriceListModal: true })
      },
      showManageActivityModal: (payload: { activity: ActivityModel }) => {
        setModalData('ManageActivityModal', payload)
        setState({ showManageActivityModal: true })
      },
      showUploadEntityAssetsModal: (payload: HandleCallbackActionParams) => {
        setModalData('UploadEntityAssetsModal', payload)
        setState({ showUploadEntityAssetsModal: true })
      },
      syncChangesToForks: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateProviderPriceList: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent providerPriceListId={providerPriceListId} />

        <ArchiveProviderPriceListModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ArchiveProviderPriceListModal', setState)}
          showModal={showArchiveProviderPriceListModal}
        />

        <BulkReassignAgenciesModal
          // @ts-expect-error TS2322
          callbacks={callbacks('BulkReassignAgenciesModal', setState)}
          showModal={showBulkReassignAgenciesModal}
        />

        <CreateOrEditProviderPriceListModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditProviderPriceListModal', setState)}
          showModal={showCreateOrEditProviderPriceListModal}
        />

        <ForkProviderPriceListModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ForkProviderPriceListModal', setState)}
          showModal={showForkProviderPriceListModal}
        />

        <ManageActivityModal
          callbacks={callbacks('ManageActivityModal', setState)}
          showModal={showManageActivityModal}
        />

        <UploadEntityAssetsModal
          callbacks={callbacks('UploadEntityAssetsModal', setState)}
          showModal={showUploadEntityAssetsModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
